import { useState } from 'react';

import { InfoIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Switch } from '@/components/ui/switch';
import { CampaignForm, voiceOpenerMetadata } from '@/modules/campaigns/utils';
import { senderFieldPath } from '@/modules/campaigns/utils/senderFieldPath';
import {
  DEFAULT_FIRST_MESSAGE_DELAY_MS,
  MAX_FIRST_MESSAGE_DELAY_MS,
} from '@/shared/types/campaigns';

// A number input hands back NaN for text it cannot parse, and NaN is not nullish,
// so it would survive every `??` fallback and render as an invalid value
const asDelay = (value?: number | null): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const clampDelay = (value?: number | null): number | null => {
  const delay = asDelay(value);
  if (delay === null) {
    return null;
  }
  return Math.min(Math.max(Math.trunc(delay), 0), MAX_FIRST_MESSAGE_DELAY_MS);
};

interface VoiceCallSettingsProps {
  /** Sender whose voicemail action shares this block's reset */
  senderPath: string;
  /** Whether that sender's integration has AMD on (the voicemail control shows) */
  amdEnabled?: boolean;
}

/**
 * Campaign-level opener settings for voice campaigns. The same values are
 * written to every voice sender, so the first call and the NBA follow-ups
 * cannot diverge. Rendered once, in the primary sender's voice block — these
 * are campaign-wide knobs, so a per-sender copy would just edit the same
 * fields twice.
 *
 * Both controls always show their effective value, defaults included, so there
 * is no empty or ghost state to explain. Only values that differ from the
 * default are stored (see `voiceOpenerMetadata`), which keeps a campaign that
 * kept the defaults inheriting them.
 */
export const VoiceCallSettings = ({ senderPath, amdEnabled = false }: VoiceCallSettingsProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<CampaignForm>();
  // Raw text held only while the delay field is being edited
  const [draft, setDraft] = useState<string>();

  const amdPath = senderFieldPath(senderPath, 'amdOnMachine');
  const voiceSettings = useWatch({ control, name: 'voiceSettings' });
  const amdOnMachine = useWatch({ control, name: amdPath });
  // Derived from what would actually be stored, so the reset appears exactly
  // when the campaign overrides a default and cannot drift from that rule.
  // The voicemail action only counts while its control is actually shown.
  const isCustomized =
    Object.keys(voiceOpenerMetadata(voiceSettings)).length > 0 ||
    (amdEnabled && Boolean(amdOnMachine));

  // null, not undefined: removing the keys outright leaves the controls showing
  // their old values. Re-validate so a stale range error does not linger.
  const resetToDefaults = () => {
    setDraft(undefined);
    setValue('voiceSettings.firstMessageInterruptible', null, { shouldValidate: true });
    setValue('voiceSettings.firstMessageDelayMs', null, { shouldValidate: true });
    if (amdEnabled) {
      setValue(amdPath, null);
    }
  };

  return (
    <>
      <FormField
        control={control}
        name="voiceSettings.firstMessageInterruptible"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start justify-between gap-3">
            <div className="space-y-1">
              <FormLabel className="text-sm leading-none">
                {t('campaigns.build.voice_settings.interruptible_label')}
              </FormLabel>
              <FormDescription>
                {t('campaigns.build.voice_settings.interruptible_description')}
              </FormDescription>
            </div>
            <FormControl>
              {/* Off is the platform default, so an unset value reads as off */}
              <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="voiceSettings.firstMessageDelayMs"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <FormLabel className="flex items-center gap-1.5 text-sm leading-none">
              {t('campaigns.build.voice_settings.delay_label')}
              <InfoTooltip content={t('campaigns.build.voice_settings.delay_amd_hint')}>
                {/* A real button so the hint is reachable by keyboard and screen readers */}
                <button
                  type="button"
                  aria-label={t('campaigns.build.voice_settings.delay_amd_hint')}
                  className="inline-flex"
                >
                  <InfoIcon className="size-3.5 text-foreground-muted" />
                </button>
              </InfoTooltip>
            </FormLabel>
            <FormDescription>
              {t('campaigns.build.voice_settings.delay_description')}
            </FormDescription>
            {/* FormControl must clone the input itself, not the group div, so the
                label's htmlFor and the error ids land on the field */}
            <InputGroup>
              <FormControl>
                <InputGroupInput
                  type="number"
                  min={0}
                  max={MAX_FIRST_MESSAGE_DELAY_MS}
                  step={100}
                  // Show the effective value, so stepping and editing both start
                  // from the number the user can actually see
                  value={draft ?? asDelay(field.value) ?? DEFAULT_FIRST_MESSAGE_DELAY_MS}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // While the field is focused the text is the user's to edit;
                    // normalizing on every keystroke fights them mid-edit
                    setDraft(raw);
                    field.onChange(raw === '' ? null : asDelay(Number(raw)));
                  }}
                  onBlur={() => {
                    setDraft(undefined);
                    // Clamp on the way out: Save & Exit skips validation. An
                    // emptied or unparseable field falls back to the default.
                    field.onChange(clampDelay(field.value));
                    field.onBlur();
                  }}
                />
              </FormControl>
              <InputGroupAddon align="inline-end">{t('common.milliseconds')}</InputGroupAddon>
            </InputGroup>
            <FormMessage />
          </FormItem>
        )}
      />

      {isCustomized ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto self-start p-0 text-primary hover:bg-transparent"
          onClick={resetToDefaults}
        >
          {t('campaigns.build.voice_settings.use_default')}
        </Button>
      ) : null}
    </>
  );
};
